import React, {useEffect, useState} from 'react';
import {
    Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    LinearProgress, Radio, RadioGroup, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import {Add, Info} from '@mui/icons-material';
import {Controller} from 'react-hook-form';
import CountrySelect from '../utils/CountrySelect';
import AffiliateNetworkSelect from './AffiliateNetworkSelect';
import CurrencySelect from './CurrencySelect';
import CopyContentToClipboard from '../utils/CopyContentToClipboard';
import UrlWithParams from '../input/UrlWithParams';
import {sharedAffiliateNetworkService} from '../../service/sharedAffiliateNetworkService';
import {GetDefaultTrackingDomain, GetPostbackLink, GetReplacedPostbackDomainLink} from '../../helpers/common';
import {postbackDomain} from '../../helpers/config';
import AffiliateNetworkPopUp from '../affiliate-networks/AffiliateNetworkPopUp';
import TimezoneSelect from '../timezone/TimezoneSelect';
import SelectOffer from './SelectOffer';
import WorkspaceDropdown from '../workspace/WorkspaceDropdown';
import ReactHookTagsSelect from '../tags/ReactHookTagsSelect';
import {OfferSection, offerSelectPaperStyles} from './OfferDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';

const fieldGrid = {display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2, minWidth: 0};
const copyFieldStyles = {minWidth: 0, '& .MuiOutlinedInput-root': {fontFamily: 'monospace'},
    '& .MuiBox-root': {minWidth: 0}, '& > div > .MuiBox-root': {alignItems: 'center'},
    '& > div > .MuiBox-root > .MuiBox-root:last-child': {mt: 0}};

function ShopifyWebhooks({trackingDomain}) {
    return <Box sx={{display: 'grid', gap: 2, minWidth: 0}}>
        <Typography sx={{fontSize: 13, color: 'text.secondary', lineHeight: 1.65}}>
            In Shopify, go to Settings → Notifications → Webhooks and create a webhook. Use JSON format and webhook API version 2025-07.
        </Typography>
        <Box sx={copyFieldStyles}><CopyContentToClipboard label="Standard sales webhook" copyText={`https://${trackingDomain}/webhook/shopify`}/></Box>
        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>Event: Order creation.</Typography>
        <Typography sx={{fontSize: 13, fontWeight: 600}}>Custom Conversion webhooks</Typography>
        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>Add these webhooks when you use the corresponding Custom Conversion events.</Typography>
        <Box sx={copyFieldStyles}><CopyContentToClipboard label="Purchase webhook" copyText={`https://${trackingDomain}/webhook/shopify?ce=Purchase`}/></Box>
        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>Event: Order creation.</Typography>
        <Box sx={copyFieldStyles}><CopyContentToClipboard label="Checkout webhook" copyText={`https://${trackingDomain}/webhook/shopify?ce=InitiateCheckout`}/></Box>
        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>Event: Checkout creation.</Typography>
    </Box>;
}

export const OfferForm = ({title, watch, control, register, handleClose, requestError, errors, handleSubmit, onSubmit,
    loading, setValue, getValues, offerData, submitLabel = 'Save changes'}) => {
    const formId = React.useId();
    const [postbackLink, setPostbackLink] = useState(GetReplacedPostbackDomainLink);
    const [postbackLoading, setPostbackLoading] = useState(false);
    const [postbackError, setPostbackError] = useState('');
    const [openAffNetworkPopUp, setOpenAffNetworkPopUp] = useState(false);
    const network = watch('affiliateNetwork');
    const sharedNetworkId = network?.sharedAffiliateNetworkId;
    const protectionToken = network?.usePostbackProtection ? network?.postbackProtectionToken : '';
    const shopify = !!network?.label?.toLowerCase().includes('shopify');
    const displayName = (watch('country')?.label || 'Global') + ' - ' + (watch('name') || 'Offer name');

    useEffect(() => {
        let active = true;
        const protectedLink = link => protectionToken ? link + '&protectionToken=' + protectionToken : link;
        setPostbackError('');
        setPostbackLink(protectedLink(GetPostbackLink()));
        setPostbackLoading(false);
        if (sharedNetworkId > 0 && !shopify) {
            setPostbackLoading(true);
            sharedAffiliateNetworkService.find(sharedNetworkId).then(response => {
                if (active && response?.id > 0) setPostbackLink(protectedLink(GetPostbackLink(
                    response.clickIdPlaceholder, response.payoutPlaceholder, response.transactionIdPlaceholder, response.statusPlaceholder)));
            }).catch(() => {
                if (active) setPostbackError('Unable to load network tokens. Check the URL placeholders before copying.');
            }).finally(() => {if (active) setPostbackLoading(false);});
        }
        return () => {active = false;};
    }, [sharedNetworkId, protectionToken, shopify]);

    return <>
        <DialogTitle id="offer-form-title">{title}
            <SettingsDialogDescription>Manage offer details, conversion tracking, payout and daily limits.</SettingsDialogDescription>
        </DialogTitle>
        {loading && <LinearProgress aria-label="Loading offer" sx={{mx: {xs: 2, sm: 3}, borderRadius: 1}}/>}
        <DialogContent>
            {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
            <Box component="form" id={formId} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Box component="fieldset" disabled={loading} sx={{border: 0, p: 0, m: 0, minWidth: 0, display: 'grid', gap: 2}}>
                    <OfferSection title="Offer details">
                        <Box sx={fieldGrid}>
                            <Box sx={{minWidth: 0}}><WorkspaceDropdown control={control} workspaceId={offerData?.workspaceId} setValue={setValue}
                                validate={value => value?.value > 0 || 'Please select a workspace.'}/></Box>
                            <TextField {...register('name', {required: true, maxLength: 255, minLength: 3})} error={!!errors.name}
                                helperText={errors.name ? 'Enter a name between 3 and 255 characters.' : undefined}
                                label="Name *" placeholder="Offer name" fullWidth size="small" slotProps={{inputLabel: {shrink: true}}}/>
                        </Box>

                        <Box display="flex" flexDirection="row" flexWrap="noWrap">
                            <Box flexGrow={1}>
                                <CountrySelect control={control} compact/>
                            </Box>
                            <Box sx={{pt: 1, pr: 1}}>
                                <Tooltip
                                    disableFocusListener
                                    sx={{
                                        marginLeft: 2,
                                        fontSize: "18px",
                                        position: "relative",
                                        cursor: "pointer"
                                    }}
                                    title="Country helps organize your offers and prefixes their names. It does not affect traffic distribution.">
                                    <Info color={"primary"} fontSize={"small"}/>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box sx={{minWidth: 0}}>
                            <Typography sx={{fontSize: 11, color: 'text.secondary', mb: 0.5}}>
                                Display name
                            </Typography>
                            <Typography sx={{fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere'}}>
                                {displayName}
                            </Typography>
                        </Box>
                        <Box sx={{minWidth: 0, '& .MuiAutocomplete-root > .MuiBox-root': {alignItems: 'center'},
                            '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:first-of-type': {minWidth: 0},
                            '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:last-child': {py: 0, '& > div': {paddingTop: '0 !important'}}}}>
                            <ReactHookTagsSelect control={control} inputName="tags" inputLabel="Tags" inputLabelShrink getValues={getValues}/>
                        </Box>
                    </OfferSection>

                    <OfferSection title="Offer URL" description="Enter the destination URL. Add tokens to pass tracking and visit data to your offer.">
                        <UrlWithParams label="Offer URL *" name="url" register={register} errors={errors} watch={watch} setValue={setValue} compact urlType="offer"/>
                    </OfferSection>

                    <OfferSection title="Affiliate network" description="Select the network receiving your traffic and configure conversion tracking.">
                        <Box sx={{display: 'flex', flexWrap: {xs: 'wrap', sm: 'nowrap'}, gap: 1.5, alignItems: 'center', minWidth: 0}}>
                            <Box sx={{flex: '1 1 240px', minWidth: 0}}><AffiliateNetworkSelect control={control}/></Box>
                            <Button type="button" variant="outlined" color="inherit" startIcon={<Add/>} disabled={loading}
                                onClick={() => setOpenAffNetworkPopUp(true)} sx={{borderColor: 'divider', flexShrink: 0}}>New network</Button>
                        </Box>
                        {shopify ? <ShopifyWebhooks trackingDomain={GetDefaultTrackingDomain() || postbackDomain}/> : <>
                            {postbackError && <Alert severity="warning">{postbackError}</Alert>}
                            {postbackLoading ? <Box role="status" sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 1}}>
                                <CircularProgress size={18}/><Typography sx={{fontSize: 13, color: 'text.secondary'}}>Loading postback URL…</Typography>
                            </Box> : <Box sx={copyFieldStyles}><CopyContentToClipboard label="Postback URL" copyText={postbackLink}/></Box>}
                            <Typography sx={{fontSize: 12, color: 'text.secondary', lineHeight: 1.65}}>Copy this URL into your affiliate network to receive conversions. Replace any remaining placeholders with network tokens.</Typography>
                        </>}
                    </OfferSection>

                    <OfferSection title="RSOC tracking" description="Use a channel, campaign or brand ID to track revenue from Sedo, Ads, Predicto, AirFind or ExplorAds.">
                        <TextField {...register('channelId', {maxLength: 100, minLength: 1})} error={!!errors.channelId}
                            helperText={errors.channelId ? 'Use at most 100 characters.' : 'Optional for offers that do not use RSOC.'}
                            label="Channel / Campaign / Brand ID" placeholder="Enter your RSOC ID" fullWidth size="small" slotProps={{inputLabel: {shrink: true}}}/>
                    </OfferSection>

                    <OfferSection title="Payout" description="Use the payout received in conversions or set a fixed amount for this offer.">
                        <Controller name="payoutType" control={control} defaultValue="auto" render={({field}) => <RadioGroup {...field} row aria-label="Payout type"
                            sx={{gap: 2, '& .MuiFormControlLabel-root': {mr: 0}, '& .MuiFormControlLabel-label': {fontSize: 13}}}>
                            <FormControlLabel value="auto" control={<Radio size="small"/>} label="Automatic"/>
                            <FormControlLabel value="manual" control={<Radio size="small"/>} label="Manual"/>
                        </RadioGroup>}/>
                        <Box sx={fieldGrid}>
                            {watch('payoutType') === 'manual' && <TextField {...register('payoutSum', {required: true, pattern: /^(\d+)(?:[.]\d+)?$/})}
                                error={!!errors.payoutSum} helperText={errors.payoutSum ? 'Enter a valid payout amount, such as 2.50.' : undefined}
                                label="Payout amount *" placeholder="2.50" fullWidth size="small" slotProps={{inputLabel: {shrink: true}, htmlInput: {inputMode: 'decimal'}}}/>}
                            <CurrencySelect control={control}/>
                        </Box>
                    </OfferSection>

                    <OfferSection title="Daily cap" description="Limit conversions within a 24-hour period and redirect traffic to another offer when the limit is reached.">
                        <Controller name="enableDailyCap" control={control} defaultValue={false} render={({field: {value, onChange, ref, ...field}}) => <FormControlLabel
                            labelPlacement="start" sx={{m: 0, width: '100%', justifyContent: 'space-between', gap: 2, '& .MuiFormControlLabel-label': {fontSize: 13, fontWeight: 600}, '& .MuiSwitch-root': {mr: -1}}}
                            label="Enable daily cap" control={<Switch {...field} inputRef={ref} checked={!!value} onChange={event => onChange(event.target.checked)} color="primary"/>}/>}/>
                        {watch('enableDailyCap') && <>
                            <Box sx={fieldGrid}>
                                <TextField {...register('dailyCapLimit', {required: true, maxLength: 12, minLength: 1, pattern: /^\d*?$/})}
                                    error={!!errors.dailyCapLimit} helperText={errors.dailyCapLimit ? 'Enter a whole number of at most 12 digits.' : undefined}
                                    label="Conversion limit *" placeholder="e.g. 100" type="number" fullWidth size="small" slotProps={{inputLabel: {shrink: true}}}/>
                                <TimezoneSelect control={control} selectedValue={offerData?.dailyCapTimezone} setValue={setValue} slotProps={{paper: {sx: offerSelectPaperStyles}}} inputLabelShrink/>
                            </Box>
                            <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>Match the timezone used by your affiliate network or advertiser.</Typography>
                            <SelectOffer control={control} errors={errors} offerData={offerData} setValue={setValue}/>
                            <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>Conversions must be tracked correctly for the daily cap to work.</Typography>
                        </>}
                    </OfferSection>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button type="button" color="inherit" disabled={loading} onClick={handleClose}>Cancel</Button>
            <Button type="submit" form={formId} variant="contained" disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit"/> : undefined}>{loading ? 'Please wait…' : submitLabel}</Button>
        </DialogActions>
        {openAffNetworkPopUp && <AffiliateNetworkPopUp open onClose={() => setOpenAffNetworkPopUp(false)}/>}
    </>;
};
